import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { AuthenticationService } from '@servicios/authentication.service';
import { Roles } from '@modelos/interfaces/operation-result-types';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ItemFormComponent } from '../table-items/item-form/item-form.component';
import { SkeletonModule } from 'primeng/skeleton';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-item-detail',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ItemFormComponent,
    SkeletonModule
  ],
  templateUrl: './item-detail.component.html',
  styleUrls: ['./item-detail.component.scss']
})
export class ItemDetailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private inventoryService = inject(InventoryService);
  private authService = inject(AuthenticationService);
  private messageService = inject(MessageService);
  private location = inject(Location);
  private subs = new Subscription();

  itemId = signal<string | null>(null);
  item = signal<any | null>(null);
  mode = signal<'view' | 'edit' | 'create'>('edit');
  loading = signal(true);
  activeTab = signal<number>(0);

  get canManageInventory(): boolean {
    const roles = this.authService.CURRENT_USER_ROLES || [];
    return roles.includes(Roles.ADMIN) || roles.includes(Roles.INVENTORY_MANAGER);
  }

  ngOnInit() {
    const paramsSub = this.route.params.subscribe(params => {
      let id = params['id'];

      // Fix for accidentally URL-encoded query strings (e.g., %3Fmode%3Dedit)
      if (id && id.includes('?')) {
        const parts = id.split('?');
        id = parts[0];
      }

      if (id && id !== 'new') {
        this.itemId.set(id);
        this.mode.set(this.canManageInventory ? 'edit' : 'view');
        this.loadItem(id);
      } else {
        if (!this.canManageInventory) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Acceso Restringido',
            detail: 'No posees permisos para dar de alta nuevos artículos.'
          });
          this.router.navigate(['/inventory/items']);
          return;
        }

        this.itemId.set(null);
        this.mode.set('create');

        // Check for type in query params
        const qSub = this.route.queryParams.subscribe(qParams => {
          const type = qParams['type'] || 'PRODUCT';
          this.item.set({
            type,
            currency: 'USD',
            stock: 0,
            min_stock: 0,
            max_stock: 0,
            alert_stock: 0
          });
          this.activeTab.set(99);
          this.loading.set(false);
        });
        this.subs.add(qSub);
      }
    });
    this.subs.add(paramsSub);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  async loadItem(id: string) {
    try {
      this.loading.set(true);
      const res = await this.inventoryService.getOne('items', id);
      this.item.set(res);
      this.mode.set(this.canManageInventory ? 'edit' : 'view');
      this.loading.set(false);
    } catch (error) {
      console.error('Error loading item:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar la información del artículo.'
      });
      // Use router navigate instead of goBack() in case of direct F5 reload with no history
      this.router.navigate(['/inventory/items']);
    }
  }

  async onSave(payload: any) {
    if (!this.canManageInventory) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No posees permisos para modificar artículos.'
      });
      return;
    }

    try {
      if (this.itemId()) {
        // Update
        const payloadToUpdate = { ...payload };
        // Remove prohibited fields (same as in TableItemsComponent)
        const fieldsToRemove = ['createdAt', 'updatedAt', 'brand', 'form', 'suppliers', 'controlledBy'];
        fieldsToRemove.forEach(f => delete (payloadToUpdate as any)[f]);

        await this.inventoryService.update('items', this.itemId()!,  payloadToUpdate);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Artículo actualizado correctamente' });
        this.loadItem(this.itemId()!);
      } else {
        // Create
        const res = await this.inventoryService.create('items', payload);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Artículo creado correctamente' });
        // Navigate to the new item's detail page
        this.router.navigate(['/inventory/items', res.id]);
      }
    } catch (error: any) {
      console.error('Error saving item:', error);
      let detail = 'No se pudo guardar el artículo';
      if (Array.isArray(error?.error?.message)) {
        detail = error.error.message.join('. ');
      } else if (typeof error?.error?.message === 'string') {
        detail = error.error.message;
      } else if (error?.message) {
        detail = error.message;
      }
      this.messageService.add({ severity: 'error', summary: 'Error al Guardar', detail, life: 7000 });
    }
  }

  onCancel() {
    this.goBack();
  }

  goBack() {
    this.router.navigate(['/inventory/items']);
  }

  onModeChange(newMode: 'view' | 'edit' | 'create') {
    if ((newMode === 'edit' || newMode === 'create') && !this.canManageInventory) {
      this.mode.set('view');
      return;
    }
    this.mode.set(newMode);
  }
}
